import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Grid,
  TextField,
  Alert,
  Snackbar,
  Card,
  CardMedia,
  IconButton,
  InputAdornment,
  FormControl,
  OutlinedInput,
  FormHelperText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";
import { BASE_URL } from "../config/config";
import {
  Close,
  CloudUpload,
  CheckCircle,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  canUpdatePrimaryService,
  canAddPrimaryService,
} from "../../utility/permissionUtils";

const AddServiceModal = ({
  getAllServicesExists,
  getAllActivePrimaryServices,
  showDialog,
  isUpdate = false,
  serviceId = null,
  serviceNameProp = "",
}) => {
  const [open, setOpen] = useState(false);
  const [serviceName, setServiceName] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Internal snackbar state as fallback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const globalState = useSelector((state) => state.centerOnboardReducer);
  const access_token = globalState?.userTokens?.accessToken;

  // Get user permissions for services
  const adminState = useSelector((state) => state.AdminReducer);
  const userPermissions = adminState?.adminDetails?.role_id?.permissions;

  useEffect(() => {
    if (isUpdate && serviceNameProp) {
      setServiceName(serviceNameProp);
    }
  }, [isUpdate, serviceNameProp]);

  const handleOpen = () => {
    // Check permissions before opening modal
    if (isUpdate && !canUpdatePrimaryService) {
      showNotification("error", "You don't have permission to update services");
      return;
    }
    if (!isUpdate && !canAddPrimaryService) {
      showNotification("error", "You don't have permission to add services");
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
    setImagePreview(null);
    setErrors({});
    if (!isUpdate) setServiceName("");
  };

  // Show notification function
  const showNotification = (severity, message) => {
    if (typeof showDialog === "function") {
      // Use parent's showDialog if available
      showDialog(true, severity, message);
    } else {
      // Fallback to internal snackbar
      setSnackbar({
        open: true,
        message: message,
        severity: severity,
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleServiceNameChange = (event) => {
    setServiceName(event.target.value.toUpperCase());
    if (errors.serviceName) {
      setErrors({ ...errors, serviceName: "" });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match("image.*")) {
      setErrors({ ...errors, image: "Please select an image file" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors({ ...errors, image: "Image size must be less than 5MB" });
      return;
    }

    setSelectedImage(file);
    setErrors({ ...errors, image: "" });

    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setErrors({ ...errors, image: "" });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!serviceName.trim()) {
      newErrors.serviceName = "Please provide a service name";
    }

    if (!selectedImage && !isUpdate) {
      newErrors.image = "Please select an image";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Final permission check before submission
    if (isUpdate && !canUpdatePrimaryService) {
      showNotification("error", "You don't have permission to update services");
      return;
    }
    if (!isUpdate && !canAddPrimaryService) {
      showNotification("error", "You don't have permission to add services");
      return;
    }

    if (!validateForm()) return;

    setIsSubmitting(true);

    const data = {
      service_name: serviceName.trim(),
    };

    if (isUpdate) {
      data.service_object_id = serviceId;
    }

    const formData = new FormData();
    formData.append("data", JSON.stringify(data));
    if (selectedImage) {
      formData.append("file", selectedImage);
    }

    try {
      const endpoint = isUpdate
        ? "admin/update-service"
        : "admin/create-services";

      const response = await axios.post(`${BASE_URL}${endpoint}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-access-token": access_token,
        },
      });

      if (response.status === 200) {
        // Use the unified notification function
        showNotification(
          "success",
          `Service ${isUpdate ? "updated" : "created"} successfully`
        );

        handleClose();

        // Refresh the services list
        if (getAllServicesExists) {
          getAllServicesExists();
        }
        if (getAllActivePrimaryServices) {
          getAllActivePrimaryServices();
        }
      } else {
        throw new Error("Operation failed");
      }
    } catch (error) {
      console.error("Error:", error);
      showNotification(
        "error",
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render the button if user doesn't have permission
  if (
    (isUpdate && !canUpdatePrimaryService) ||
    (!isUpdate && !canAddPrimaryService)
  ) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<AddIcon />}
        onClick={handleOpen}
        className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-700 hover:to-blue-600"
        sx={{ textTransform: "none" }}
      >
        {isUpdate ? "Edit Service" : "Add a Service"}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle className="flex justify-between items-center pb-2 border-b border-gray-200">
          <Typography variant="h6" className="font-semibold text-gray-800">
            {isUpdate ? "Edit Service" : "Add a Service"}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            className="text-gray-500 hover:text-gray-700"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className="mt-4">
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Service Name"
                  value={serviceName}
                  onChange={handleServiceNameChange}
                  variant="outlined"
                  error={!!errors.serviceName}
                  helperText={errors.serviceName}
                  className="mb-4"
                  InputProps={{
                    className: "rounded-lg",
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <div className="mb-2">
                  <Typography variant="body2" className="text-gray-600 mb-1">
                    Service Image{" "}
                    {!isUpdate && <span className="text-red-500">*</span>}
                  </Typography>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!errors.image}
                  >
                    <OutlinedInput
                      id="service-image"
                      type="file"
                      inputProps={{
                        accept: "image/*",
                        className: "py-2.5",
                      }}
                      onChange={handleImageChange}
                      endAdornment={
                        <InputAdornment position="end">
                          <Button
                            component="span"
                            startIcon={<CloudUpload />}
                            className="normal-case bg-gray-100 hover:bg-gray-200 text-gray-700"
                          >
                            Browse
                          </Button>
                        </InputAdornment>
                      }
                    />
                    {errors.image && (
                      <FormHelperText className="text-red-600">
                        {errors.image}
                      </FormHelperText>
                    )}
                    <FormHelperText className="text-gray-500 text-xs">
                      Select an image for the service (Max 5MB)
                    </FormHelperText>
                  </FormControl>
                </div>
              </Grid>

              {imagePreview && (
                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    className="relative overflow-hidden rounded-lg"
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={imagePreview}
                      alt="Service preview"
                      className="object-contain"
                    />
                    <IconButton
                      size="small"
                      onClick={removeImage}
                      className="absolute top-2 right-2 bg-white hover:bg-gray-100 shadow-md"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Card>
                </Grid>
              )}
            </Grid>
          </form>
        </DialogContent>

        <DialogActions className="px-6 py-4 border-t border-gray-200">
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isSubmitting}
            className="normal-case rounded-lg px-4 py-2"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? <CircularProgress size={16} /> : <CheckCircle />
            }
            className="normal-case rounded-lg px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-700 hover:to-blue-600"
          >
            {isSubmitting
              ? "Processing..."
              : isUpdate
              ? "Update Service"
              : "Create Service"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fallback Snackbar - only shows if parent doesn't provide showDialog */}
      {!showDialog && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            className="w-full shadow-lg"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default AddServiceModal;
